import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { FileUp, FileText, CheckCircle, XCircle, AlertCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BATCH_SIZE = 50; // Upload 50 files at a time

export const ImportPDFModal = ({ open, onOpenChange, onSuccess }) => {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [result, setResult] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setFiles(prev => [...prev, ...pdfFiles]);
    setResult(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadBatch = async (batch, headers) => {
    const formData = new FormData();
    batch.forEach(file => {
      formData.append('files', file);
    });
    
    const res = await axios.post(`${API}/pdfs/upload`, formData, { 
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data'
      },
      timeout: 300000 // 5 minutes timeout per batch
    });
    return res.data;
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    try {
      setUploading(true);
      setProgress({ current: 0, total: files.length, percent: 0 });
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Aggregate results from all batches
      const aggregatedResult = {
        associated: [],
        not_found: [],
        already_exists: [],
        errors: []
      };
      
      // Process in batches
      const totalBatches = Math.ceil(files.length / BATCH_SIZE);
      
      for (let i = 0; i < totalBatches; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, files.length);
        const batch = files.slice(start, end);
        
        try {
          const batchResult = await uploadBatch(batch, headers);
          
          // Aggregate results
          aggregatedResult.associated.push(...(batchResult.details?.associated || []));
          aggregatedResult.not_found.push(...(batchResult.details?.not_found || []));
          aggregatedResult.already_exists.push(...(batchResult.details?.already_exists || []));
          aggregatedResult.errors.push(...(batchResult.details?.errors || []));
          
        } catch (batchError) {
          console.error(`Batch ${i + 1} failed:`, batchError);
          // Add files from failed batch to errors
          batch.forEach(file => {
            aggregatedResult.errors.push({
              filename: file.name,
              error: "Échec du lot d'upload"
            });
          });
        }
        
        // Update progress
        const processed = end;
        setProgress({
          current: processed,
          total: files.length,
          percent: Math.round((processed / files.length) * 100)
        });
      }
      
      // Set final result
      setResult({
        success: true,
        associated_count: aggregatedResult.associated.length,
        not_found_count: aggregatedResult.not_found.length,
        already_exists_count: aggregatedResult.already_exists.length,
        error_count: aggregatedResult.errors.length,
        details: aggregatedResult
      });
      
      if (aggregatedResult.associated.length > 0) {
        toast.success(`${aggregatedResult.associated.length} PDF(s) associé(s) avec succès`);
        onSuccess?.();
      }
      if (aggregatedResult.not_found.length > 0) {
        toast.warning(`${aggregatedResult.not_found.length} PDF(s) sans correspondance`);
      }
      
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'import";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setResult(null);
      setProgress({ current: 0, total: 0, percent: 0 });
      onOpenChange(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <FileUp className="w-5 h-5 text-blue-600" />
            Importer des PDFs
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Glissez vos fichiers PDF. Ils seront automatiquement associés aux factures correspondantes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
          {/* Dropzone */}
          {!uploading && !result && (
            <div
              {...getRootProps()}
              className={`dropzone p-6 rounded-lg text-center cursor-pointer ${isDragActive ? 'active' : ''}`}
              data-testid="pdf-dropzone"
            >
              <input {...getInputProps()} />
              <div className="space-y-2">
                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center">
                  <FileUp className={`w-6 h-6 ${isDragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-foreground text-sm">
                    {isDragActive ? 'Déposez les fichiers ici' : 'Glissez vos fichiers PDF ici'}
                  </p>
                  <p className="text-xs text-muted-foreground">ou cliquez pour parcourir (import illimité)</p>
                </div>
              </div>
            </div>
          )}

          {/* Files List */}
          {files.length > 0 && !result && !uploading && (
            <div className="flex-1 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-medium text-foreground">{files.length} fichier(s)</span>
                  <span className="text-sm text-muted-foreground ml-2">({formatFileSize(totalSize)})</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFiles([])}
                  className="text-muted-foreground"
                >
                  Tout effacer
                </Button>
              </div>
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {files.slice(0, 100).map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="text-sm text-foreground truncate">{file.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="text-muted-foreground hover:text-red-500 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {files.length > 100 && (
                    <div className="text-center text-sm text-muted-foreground py-2">
                      ... et {files.length - 100} autres fichiers
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-4 py-8">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-lg font-medium text-foreground">Import en cours...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {progress.current} / {progress.total} fichiers traités
                </p>
              </div>
              <div className="space-y-2">
                <Progress value={progress.percent} className="h-3" />
                <p className="text-center text-sm font-medium text-blue-600">{progress.percent}%</p>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                Ne fermez pas cette fenêtre pendant l'import
              </p>
            </div>
          )}

          {/* Result */}
          <AnimatePresence mode="wait">
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex-1 overflow-hidden"
              >
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-green-600">{result.associated_count}</p>
                      <p className="text-xs text-green-700">Associés</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-blue-600">{result.already_exists_count || 0}</p>
                      <p className="text-xs text-blue-700">Déjà importés</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-amber-600">{result.not_found_count}</p>
                      <p className="text-xs text-amber-700">Non trouvés</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-red-500">{result.error_count}</p>
                      <p className="text-xs text-red-600">Erreurs</p>
                    </div>
                  </div>

                  {/* Details */}
                  <ScrollArea className="h-48 border rounded-lg">
                    <div className="p-3 space-y-3">
                      {/* Associated - show first 50 */}
                      {result.details.associated.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-green-700 uppercase tracking-wider mb-2">
                            ✅ PDFs associés ({result.details.associated.length})
                          </p>
                          <div className="space-y-1">
                            {result.details.associated.slice(0, 50).map((item, index) => (
                              <div key={index} className="flex items-center justify-between text-sm p-2 bg-green-50 rounded">
                                <span className="text-foreground truncate max-w-[200px]">{item.filename}</span>
                                <Badge variant="outline" className="text-xs bg-white">
                                  {item.invoice_numero}
                                </Badge>
                              </div>
                            ))}
                            {result.details.associated.length > 50 && (
                              <p className="text-xs text-center text-green-600 py-1">
                                ... et {result.details.associated.length - 50} autres
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Already exists - show first 20 */}
                      {result.details.already_exists?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-blue-700 uppercase tracking-wider mb-2">
                            📁 Déjà importés ({result.details.already_exists.length})
                          </p>
                          <div className="space-y-1">
                            {result.details.already_exists.slice(0, 20).map((item, index) => (
                              <div key={index} className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded">
                                <span className="text-foreground truncate max-w-[200px]">{item.filename}</span>
                                <Badge variant="outline" className="text-xs bg-white">
                                  {item.invoice_numero}
                                </Badge>
                              </div>
                            ))}
                            {result.details.already_exists.length > 20 && (
                              <p className="text-xs text-center text-blue-600 py-1">
                                ... et {result.details.already_exists.length - 20} autres
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Not Found - show first 20 */}
                      {result.details.not_found.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-2">
                            ⚠️ PDFs sans correspondance ({result.details.not_found.length})
                          </p>
                          <div className="space-y-1">
                            {result.details.not_found.slice(0, 20).map((filename, index) => (
                              <div key={index} className="text-sm p-2 bg-amber-50 rounded text-amber-800 truncate">
                                {filename}
                              </div>
                            ))}
                            {result.details.not_found.length > 20 && (
                              <p className="text-xs text-center text-amber-600 py-1">
                                ... et {result.details.not_found.length - 20} autres
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Errors */}
                      {result.details.errors.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-600 uppercase tracking-wider mb-2">
                            ❌ Erreurs ({result.details.errors.length})
                          </p>
                          <div className="space-y-1">
                            {result.details.errors.slice(0, 10).map((item, index) => (
                              <div key={index} className="text-sm p-2 bg-red-50 rounded text-red-800">
                                {item.filename}: {item.error}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={uploading}
            >
              {result ? 'Fermer' : 'Annuler'}
            </Button>
            {!result && !uploading && (
              <Button
                onClick={handleUpload}
                disabled={files.length === 0}
                className="flex-1 btn-primary bg-primary text-primary-foreground"
                data-testid="upload-pdfs-btn"
              >
                Importer {files.length} PDF{files.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportPDFModal;
